import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  ArrowLeft, 
  Download, 
  TrendingUp, 
  Users, 
  Banknote,
  Target,
  Award,
  BarChart3,
  Calendar,
  Loader2,
  Settings,
  Check,
  FileSpreadsheet,
  FileText,
  CalendarRange
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const StatsPage = ({ onBack }) => {
  const { language } = useLanguage();
  const { user, token } = useAuth();
  const [stats, setStats] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showTargetDialog, setShowTargetDialog] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [targetValue, setTargetValue] = useState('');
  const [salesCountTargetValue, setSalesCountTargetValue] = useState('');
  const [savingTarget, setSavingTarget] = useState(false);
  
  // Custom date range state
  const [activeTab, setActiveTab] = useState('monthly');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [customStats, setCustomStats] = useState(null);
  const [loadingCustom, setLoadingCustom] = useState(false);

  const isAdmin = user?.is_admin || user?.role === 'admin';
  const isSupervisor = user?.role === 'supervisor';
  const canViewAllStats = isAdmin || isSupervisor;

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      if (canViewAllStats) {
        const response = await axios.get(
          `${API_URL}/stats/all-agents?month=${selectedMonth}&year=${selectedYear}`
        );
        setStats(response.data);
      } else {
        const response = await axios.get(
          `${API_URL}/stats/agent/${user.id}?month=${selectedMonth}&year=${selectedYear}`
        );
        setMyStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, canViewAllStats, user?.id]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleSetTarget = (agent) => {
    setEditingAgent(agent);
    setTargetValue(agent.monthly_target?.toString() || '');
    setSalesCountTargetValue(agent.sales_count_target?.toString() || '');
    setShowTargetDialog(true);
  };

  const handleSaveTarget = async () => {
    if (!editingAgent) return;
    setSavingTarget(true);
    try {
      await axios.post(
        `${API_URL}/targets/set?month=${selectedMonth}&year=${selectedYear}`,
        {
          agent_id: editingAgent.agent_id,
          monthly_target: parseFloat(targetValue) || 0,
          sales_count_target: parseInt(salesCountTargetValue, 10) || 0,
        }
      );
      toast.success(language === 'fr' ? 'Objectifs définis !' : 'Targets set!');
      setShowTargetDialog(false);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSavingTarget(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(
        `${API_URL}/stats/export-excel?month=${selectedMonth}&year=${selectedYear}`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SADASkinCare_Stats_${selectedMonth}_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(language === 'fr' ? 'Export téléchargé!' : 'Export downloaded!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur d\'export' : 'Export error');
    } finally {
      setExporting(false);
    }
  };

  // Fetch custom date range stats
  const fetchCustomStats = useCallback(async () => {
    if (!startDate || !endDate) return;
    
    setLoadingCustom(true);
    try {
      const response = await axios.get(
        `${API_URL}/stats/custom-range?start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomStats(response.data);
    } catch (error) {
      console.error('Failed to fetch custom stats:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoadingCustom(false);
    }
  }, [startDate, endDate, token, language]);

  // Export Excel for custom range
  const handleExportExcelRange = async () => {
    setExporting(true);
    try {
      const response = await axios.get(
        `${API_URL}/stats/export-excel-range?start_date=${startDate}&end_date=${endDate}`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const startFormatted = startDate.replace(/-/g, '');
      const endFormatted = endDate.replace(/-/g, '');
      link.setAttribute('download', `SADA_Rapport_${startFormatted}_${endFormatted}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(language === 'fr' ? 'Export Excel téléchargé!' : 'Excel export downloaded!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur d\'export Excel' : 'Excel export error');
    } finally {
      setExporting(false);
    }
  };

  // Export PDF for custom range
  const handleExportPdfRange = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(
        `${API_URL}/stats/export-pdf-range?start_date=${startDate}&end_date=${endDate}`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const startFormatted = startDate.replace(/-/g, '');
      const endFormatted = endDate.replace(/-/g, '');
      link.setAttribute('download', `SADA_Rapport_${startFormatted}_${endFormatted}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(language === 'fr' ? 'Export PDF téléchargé!' : 'PDF export downloaded!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur d\'export PDF' : 'PDF export error');
    } finally {
      setExportingPdf(false);
    }
  };

  const months = [
    { value: 1, label: language === 'fr' ? 'Janvier' : 'January' },
    { value: 2, label: language === 'fr' ? 'Février' : 'February' },
    { value: 3, label: language === 'fr' ? 'Mars' : 'March' },
    { value: 4, label: language === 'fr' ? 'Avril' : 'April' },
    { value: 5, label: language === 'fr' ? 'Mai' : 'May' },
    { value: 6, label: language === 'fr' ? 'Juin' : 'June' },
    { value: 7, label: language === 'fr' ? 'Juillet' : 'July' },
    { value: 8, label: language === 'fr' ? 'Août' : 'August' },
    { value: 9, label: language === 'fr' ? 'Septembre' : 'September' },
    { value: 10, label: language === 'fr' ? 'Octobre' : 'October' },
    { value: 11, label: language === 'fr' ? 'Novembre' : 'November' },
    { value: 12, label: language === 'fr' ? 'Décembre' : 'December' },
  ];

  const years = [2024, 2025, 2026].map(y => ({ value: y, label: y.toString() }));

  const getConversionColor = (rate) => {
    if (rate >= 70) return 'text-green-600 bg-green-100';
    if (rate >= 40) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-500 bg-red-100';
  };

  const getRankBadge = (index) => {
    if (index === 0) return { color: 'bg-yellow-400 text-yellow-900', icon: '🥇' };
    if (index === 1) return { color: 'bg-gray-300 text-gray-700', icon: '🥈' };
    if (index === 2) return { color: 'bg-amber-600 text-white', icon: '🥉' };
    return null;
  };

  // Render monthly stats for admin/supervisor
  const renderMonthlyStats = () => {
    if (loading) {
      return (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#2E7D32]" />
        </div>
      );
    }

    return (
      <>
        {/* Summary for Admin/Supervisor */}
        <Card className="border-[#E7E5E4] bg-gradient-to-br from-[#2E7D32] to-[#1B5E20] text-white">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-4">
              {language === 'fr' ? 'Résumé du mois' : 'Monthly Summary'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-white/70 text-sm">{language === 'fr' ? 'Agents actifs' : 'Active Agents'}</p>
                <p className="text-2xl font-bold">{stats.filter(s => s.total_diagnoses > 0).length}</p>
              </div>
              <div>
                <p className="text-white/70 text-sm">{language === 'fr' ? 'Total Diagnostics' : 'Total Diagnoses'}</p>
                <p className="text-2xl font-bold">{stats.reduce((s, a) => s + a.total_diagnoses, 0)}</p>
              </div>
              <div>
                <p className="text-white/70 text-sm">{language === 'fr' ? 'Total Ventes' : 'Total Sales'}</p>
                <p className="text-2xl font-bold">{stats.reduce((s, a) => s + a.total_sales, 0)}</p>
              </div>
              <div>
                <p className="text-white/70 text-sm">{language === 'fr' ? 'CA Total' : 'Total Revenue'}</p>
                <p className="text-2xl font-bold">{stats.reduce((s, a) => s + a.total_revenue, 0).toLocaleString('fr-FR')} FCFA</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Agents Ranking */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-[#0F172A]">
            {language === 'fr' ? 'Classement des conseillers' : 'Agent Ranking'}
          </h2>
          
          {stats.length === 0 ? (
            <Card className="border-[#E7E5E4] border-dashed">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
                <p className="text-[#78716C]">
                  {language === 'fr' ? 'Aucune donnée pour cette période' : 'No data for this period'}
                </p>
              </CardContent>
            </Card>
          ) : (
            stats.map((agent, idx) => {
              const rankBadge = getRankBadge(idx);
              const hasTarget = agent.monthly_target > 0;
              return (
                <Card 
                  key={agent.agent_id}
                  className="border-[#E7E5E4]"
                  data-testid={`agent-rank-${idx}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        rankBadge ? rankBadge.color : 'bg-[#F5F5F4] text-[#78716C]'
                      }`}>
                        {rankBadge ? rankBadge.icon : idx + 1}
                      </div>
                      
                      <div className="flex-1">
                        <p className="font-medium text-[#0F172A]">{agent.agent_name}</p>
                        <div className="flex items-center gap-4 text-sm text-[#78716C] mt-1">
                          <span>{agent.total_diagnoses} {language === 'fr' ? 'conseils' : 'consultations'}</span>
                          <span>{agent.total_sales} {language === 'fr' ? 'ventes' : 'sales'}</span>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="font-bold text-[#0F172A]">{agent.total_revenue.toLocaleString('fr-FR')} FCFA</p>
                        <Badge className={`text-xs ${getConversionColor(agent.conversion_rate)}`}>
                          {agent.conversion_rate.toFixed(0)}%
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Target Progress */}
                    {hasTarget ? (
                      <div className="mt-3 p-3 bg-[#F5F5F4] rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-[#78716C] flex items-center gap-1">
                            <Target className="w-4 h-4" />
                            {language === 'fr' ? 'Objectif' : 'Target'}
                          </span>
                          <span className={`text-sm font-medium ${
                            agent.target_progress >= 100 ? 'text-green-600' : 
                            agent.is_on_track ? 'text-[#2E7D32]' : 'text-orange-500'
                          }`}>
                            {agent.target_progress?.toFixed(0)}%
                          </span>
                        </div>
                        <Progress 
                          value={Math.min(agent.target_progress || 0, 100)} 
                          className={`h-2 ${
                            agent.target_progress >= 100 ? '[&>div]:bg-green-500' : 
                            agent.is_on_track ? '[&>div]:bg-[#2E7D32]' : '[&>div]:bg-orange-500'
                          }`}
                        />
                        <div className="flex justify-between mt-1 text-xs text-[#78716C]">
                          <span>{agent.total_revenue.toLocaleString('fr-FR')}</span>
                          <span>{agent.monthly_target?.toLocaleString('fr-FR')} FCFA</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetTarget(agent)}
                          className="w-full mt-2 text-[#78716C] hover:text-[#0F172A]"
                        >
                          <Settings className="w-3 h-3 mr-1" />
                          {language === 'fr' ? 'Modifier objectif' : 'Edit target'}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSetTarget(agent)}
                        className="w-full mt-3 border-dashed"
                        data-testid={`set-target-${agent.agent_id}`}
                      >
                        <Target className="w-4 h-4 mr-2" />
                        {language === 'fr' ? 'Définir un objectif' : 'Set target'}
                      </Button>
                    )}
                    
                    {/* Stats bar */}
                    <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                      <div className="bg-[#F5F5F4] rounded p-2 text-center">
                        <p className="text-[#78716C]">{language === 'fr' ? 'Panier moy.' : 'Avg. basket'}</p>
                        <p className="font-semibold text-[#0F172A]">{agent.average_basket.toLocaleString('fr-FR')} FCFA</p>
                      </div>
                      <div className="bg-[#F5F5F4] rounded p-2 text-center">
                        <p className="text-[#78716C]">{language === 'fr' ? 'Conversion' : 'Conversion'}</p>
                        <p className="font-semibold text-[#0F172A]">{agent.conversion_rate.toFixed(1)}%</p>
                      </div>
                      <div className="bg-[#F5F5F4] rounded p-2 text-center">
                        <p className="text-[#78716C]">{language === 'fr' ? 'CA' : 'Revenue'}</p>
                        <p className="font-semibold text-[#0F172A]">{agent.total_revenue.toLocaleString('fr-FR')} FCFA</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </>
    );
  };

  // Render custom date range stats
  const renderCustomStats = () => {
    if (loadingCustom) {
      return (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#2E7D32]" />
          <p className="text-[#78716C] mt-2">{language === 'fr' ? 'Chargement des données...' : 'Loading data...'}</p>
        </div>
      );
    }

    if (!customStats) {
      return (
        <Card className="border-[#E7E5E4] border-dashed">
          <CardContent className="p-12 text-center">
            <CalendarRange className="w-16 h-16 text-[#E7E5E4] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#0F172A] mb-2">
              {language === 'fr' ? 'Sélectionnez une période' : 'Select a period'}
            </h3>
            <p className="text-[#78716C]">
              {language === 'fr' 
                ? 'Choisissez une date de début et de fin, puis cliquez sur "Charger les données"'
                : 'Choose a start and end date, then click "Load Data"'}
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-6">
        {/* Period header */}
        <div className="text-center">
          <Badge className="bg-[#2E7D32]/10 text-[#2E7D32] text-sm px-4 py-1">
            {customStats.period?.label}
          </Badge>
        </div>

        {/* Summary KPIs */}
        <Card className="border-[#E7E5E4] bg-gradient-to-br from-[#2E7D32] to-[#1B5E20] text-white">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-4">
              {language === 'fr' ? 'Indicateurs clés' : 'Key Indicators'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-white/70 text-sm">{language === 'fr' ? 'Diagnostics' : 'Diagnoses'}</p>
                <p className="text-2xl font-bold">{customStats.summary?.total_diagnoses || 0}</p>
              </div>
              <div>
                <p className="text-white/70 text-sm">{language === 'fr' ? 'Ventes' : 'Sales'}</p>
                <p className="text-2xl font-bold">{customStats.summary?.total_sales || 0}</p>
              </div>
              <div>
                <p className="text-white/70 text-sm">{language === 'fr' ? 'CA Total' : 'Total Revenue'}</p>
                <p className="text-2xl font-bold">{(customStats.summary?.total_revenue || 0).toLocaleString('fr-FR')} FCFA</p>
              </div>
              <div>
                <p className="text-white/70 text-sm">{language === 'fr' ? 'Panier moyen' : 'Avg. Basket'}</p>
                <p className="text-2xl font-bold">{(customStats.summary?.average_basket || 0).toLocaleString('fr-FR')} FCFA</p>
              </div>
              <div>
                <p className="text-white/70 text-sm">{language === 'fr' ? 'Conversion' : 'Conversion'}</p>
                <p className="text-2xl font-bold">{customStats.summary?.conversion_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Products */}
        {customStats.top_products?.length > 0 && (
          <Card className="border-[#E7E5E4]">
            <CardHeader>
              <CardTitle className="text-base font-medium text-[#0F172A] flex items-center gap-2">
                <Award className="w-5 h-5 text-[#2E7D32]" />
                {language === 'fr' ? 'Top 10 Produits' : 'Top 10 Products'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {customStats.top_products.map((product, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F5F5F4]">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                      idx === 1 ? 'bg-gray-100 text-gray-600' :
                      idx === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-[#F5F5F4] text-[#78716C]'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#0F172A] truncate">{product.name}</p>
                      <p className="text-xs text-[#78716C]">{product.quantity} unités</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[#0F172A]">{(product.revenue || 0).toLocaleString('fr-FR')} FCFA</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Agents Performance */}
        {customStats.agents_performance?.length > 0 && (
          <Card className="border-[#E7E5E4]">
            <CardHeader>
              <CardTitle className="text-base font-medium text-[#0F172A] flex items-center gap-2">
                <Users className="w-5 h-5 text-[#2E7D32]" />
                {language === 'fr' ? 'Performance des conseillers' : 'Agent Performance'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E7E5E4]">
                      <th className="text-left py-3 px-2 text-xs font-medium text-[#78716C]">{language === 'fr' ? 'Conseiller' : 'Agent'}</th>
                      <th className="text-right py-3 px-2 text-xs font-medium text-[#78716C]">{language === 'fr' ? 'Diagnostics' : 'Diagnoses'}</th>
                      <th className="text-right py-3 px-2 text-xs font-medium text-[#78716C]">{language === 'fr' ? 'Ventes' : 'Sales'}</th>
                      <th className="text-right py-3 px-2 text-xs font-medium text-[#78716C]">{language === 'fr' ? 'CA' : 'Revenue'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customStats.agents_performance.map((agent, idx) => (
                      <tr key={idx} className="border-b border-[#E7E5E4]/50 hover:bg-[#F5F5F4]/50">
                        <td className="py-3 px-2 font-medium text-[#0F172A]">{agent.name}</td>
                        <td className="py-3 px-2 text-right text-[#5D4E37]">{agent.total_diagnoses}</td>
                        <td className="py-3 px-2 text-right text-[#5D4E37]">{agent.total_sales}</td>
                        <td className="py-3 px-2 text-right font-semibold text-[#0F172A]">{(agent.total_revenue || 0).toLocaleString('fr-FR')} FCFA</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // Render single agent stats
  const renderAgentStats = () => {
    if (loading) {
      return (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#2E7D32]" />
        </div>
      );
    }

    if (!myStats) return null;

    return (
      <div className="space-y-6">
        {/* Personal Stats */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-[#E7E5E4]">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 text-[#2E7D32] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#0F172A]">{myStats.total_diagnoses}</p>
              <p className="text-sm text-[#78716C]">{language === 'fr' ? 'Diagnostics' : 'Diagnoses'}</p>
            </CardContent>
          </Card>
          
          <Card className="border-[#E7E5E4]">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#0F172A]">{myStats.total_sales}</p>
              <p className="text-sm text-[#78716C]">{language === 'fr' ? 'Ventes' : 'Sales'}</p>
            </CardContent>
          </Card>
          
          <Card className="border-[#E7E5E4]">
            <CardContent className="p-4 text-center">
              <Banknote className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#0F172A]">{myStats.total_revenue.toLocaleString('fr-FR')} FCFA</p>
              <p className="text-sm text-[#78716C]">{language === 'fr' ? 'Chiffre d\'affaires' : 'Revenue'}</p>
            </CardContent>
          </Card>
          
          <Card className="border-[#E7E5E4]">
            <CardContent className="p-4 text-center">
              <Target className="w-8 h-8 text-purple-500 mx-auto mb-2" />
              <p className={`text-2xl font-bold ${getConversionColor(myStats.conversion_rate).split(' ')[0]}`}>
                {myStats.conversion_rate.toFixed(1)}%
              </p>
              <p className="text-sm text-[#78716C]">{language === 'fr' ? 'Conversion' : 'Conversion'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Average Basket */}
        <Card className="border-[#E7E5E4] bg-gradient-to-br from-[#2E7D32] to-[#1B5E20] text-white">
          <CardContent className="p-6 text-center">
            <Award className="w-12 h-12 mx-auto mb-3 opacity-80" />
            <p className="text-white/70">{language === 'fr' ? 'Panier moyen' : 'Average Basket'}</p>
            <p className="text-4xl font-bold">{myStats.average_basket.toLocaleString('fr-FR')} FCFA</p>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-24">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#E7E5E4] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="text-[#78716C] hover:text-[#0F172A] transition-colors"
                data-testid="stats-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-[#0F172A]">
                  {language === 'fr' ? 'Rapports & Statistiques' : 'Reports & Statistics'}
                </h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Tabs for Monthly vs Custom Range */}
        {canViewAllStats && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-md bg-[#F5F5F4]">
              <TabsTrigger 
                value="monthly" 
                className="data-[state=active]:bg-[#2E7D32] data-[state=active]:text-white"
              >
                <Calendar className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Par mois' : 'Monthly'}
              </TabsTrigger>
              <TabsTrigger 
                value="custom" 
                className="data-[state=active]:bg-[#2E7D32] data-[state=active]:text-white"
              >
                <CalendarRange className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Période personnalisée' : 'Custom Period'}
              </TabsTrigger>
            </TabsList>

            {/* Monthly Tab Content */}
            <TabsContent value="monthly" className="mt-6 space-y-6">
              {/* Period Selector */}
              <Card className="border-[#E7E5E4]">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-3 justify-between">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-[#78716C]" />
                      <Select
                        value={selectedMonth.toString()}
                        onValueChange={(v) => setSelectedMonth(parseInt(v))}
                      >
                        <SelectTrigger className="w-[140px]" data-testid="month-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {months.map(m => (
                            <SelectItem key={m.value} value={m.value.toString()}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={selectedYear.toString()}
                        onValueChange={(v) => setSelectedYear(parseInt(v))}
                      >
                        <SelectTrigger className="w-[100px]" data-testid="year-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {years.map(y => (
                            <SelectItem key={y.value} value={y.value.toString()}>
                              {y.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {isAdmin && (
                      <Button
                        onClick={handleExport}
                        disabled={exporting}
                        variant="outline"
                        className="rounded-full border-[#2E7D32] text-[#2E7D32] hover:bg-[#2E7D32]/10"
                        data-testid="export-excel-btn"
                      >
                        {exporting ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Excel
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Monthly Stats Content */}
              {renderMonthlyStats()}
            </TabsContent>

            {/* Custom Date Range Tab Content */}
            <TabsContent value="custom" className="mt-6 space-y-6">
              {/* Date Range Selector */}
              <Card className="border-[#2E7D32]/30 bg-gradient-to-br from-[#2E7D32]/5 to-transparent">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium text-[#0F172A] flex items-center gap-2">
                    <CalendarRange className="w-5 h-5 text-[#2E7D32]" />
                    {language === 'fr' ? 'Sélectionner une période' : 'Select Period'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start-date" className="text-sm font-medium text-[#0F172A]">
                        {language === 'fr' ? 'Date de début' : 'Start Date'}
                      </Label>
                      <Input
                        id="start-date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="border-[#E7E5E4]"
                        data-testid="start-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="end-date" className="text-sm font-medium text-[#0F172A]">
                        {language === 'fr' ? 'Date de fin' : 'End Date'}
                      </Label>
                      <Input
                        id="end-date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="border-[#E7E5E4]"
                        data-testid="end-date-input"
                      />
                    </div>
                  </div>
                  
                  {/* Quick date presets */}
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: language === 'fr' ? '7 derniers jours' : 'Last 7 days', days: 7 },
                      { label: language === 'fr' ? '30 derniers jours' : 'Last 30 days', days: 30 },
                      { label: language === 'fr' ? '90 derniers jours' : 'Last 90 days', days: 90 },
                      { label: language === 'fr' ? 'Cette année' : 'This year', days: 'year' },
                    ].map((preset) => (
                      <Button
                        key={preset.days}
                        variant="outline"
                        size="sm"
                        className="text-xs border-[#2E7D32]/50 text-[#2E7D32] hover:bg-[#2E7D32]/10"
                        onClick={() => {
                          const end = new Date();
                          let start;
                          if (preset.days === 'year') {
                            start = new Date(end.getFullYear(), 0, 1);
                          } else {
                            start = new Date();
                            start.setDate(start.getDate() - preset.days);
                          }
                          setStartDate(start.toISOString().split('T')[0]);
                          setEndDate(end.toISOString().split('T')[0]);
                        }}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3 pt-2">
                    <Button
                      onClick={fetchCustomStats}
                      disabled={loadingCustom || !startDate || !endDate}
                      className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white"
                      data-testid="fetch-custom-stats-btn"
                    >
                      {loadingCustom ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <BarChart3 className="w-4 h-4 mr-2" />
                      )}
                      {language === 'fr' ? 'Charger les données' : 'Load Data'}
                    </Button>
                    
                    <Button
                      onClick={handleExportExcelRange}
                      disabled={exporting || !startDate || !endDate}
                      variant="outline"
                      className="border-[#2E7D32] text-[#2E7D32] hover:bg-[#2E7D32]/10"
                      data-testid="export-excel-range-btn"
                    >
                      {exporting ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                      )}
                      Export Excel
                    </Button>
                    
                    <Button
                      onClick={handleExportPdfRange}
                      disabled={exportingPdf || !startDate || !endDate}
                      variant="outline"
                      className="border-red-500 text-red-600 hover:bg-red-50"
                      data-testid="export-pdf-range-btn"
                    >
                      {exportingPdf ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <FileText className="w-4 h-4 mr-2" />
                      )}
                      Export PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Custom Stats Results */}
              {renderCustomStats()}
            </TabsContent>
          </Tabs>
        )}

        {/* Non-admin view (single agent stats) */}
        {!canViewAllStats && (
          <>
            <Card className="border-[#E7E5E4]">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-[#78716C]" />
                  <Select
                    value={selectedMonth.toString()}
                    onValueChange={(v) => setSelectedMonth(parseInt(v))}
                  >
                    <SelectTrigger className="w-[140px]" data-testid="month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map(m => (
                        <SelectItem key={m.value} value={m.value.toString()}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={selectedYear.toString()}
                    onValueChange={(v) => setSelectedYear(parseInt(v))}
                  >
                    <SelectTrigger className="w-[100px]" data-testid="year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y.value} value={y.value.toString()}>
                          {y.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
            {renderAgentStats()}
          </>
        )}
      </main>

      {/* Target Dialog */}
      <Dialog open={showTargetDialog} onOpenChange={setShowTargetDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-[#2E7D32]" />
              {language === 'fr' ? 'Définir l\'objectif mensuel' : 'Set Monthly Target'}
            </DialogTitle>
          </DialogHeader>
          
          {editingAgent && (
            <div className="space-y-4">
              <div className="p-3 bg-[#F5F5F4] rounded-lg">
                <p className="font-medium text-[#0F172A]">{editingAgent.agent_name}</p>
                <p className="text-sm text-[#78716C]">
                  {language === 'fr' ? 'CA actuel' : 'Current revenue'}: {editingAgent.total_revenue?.toLocaleString('fr-FR')} FCFA
                </p>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#0F172A]">
                  {language === 'fr' ? 'Objectif de CA (FCFA)' : 'Revenue Target (FCFA)'}
                </label>
                <Input
                  type="number"
                  value={targetValue}
                  onChange={(e) => setTargetValue(e.target.value)}
                  placeholder={language === 'fr' ? 'Ex: 500000' : 'Ex: 500000'}
                  className="text-lg"
                  data-testid="target-input"
                />
                <p className="text-xs text-[#78716C]">
                  {language === 'fr' 
                    ? 'Objectif de chiffre d\'affaires mensuel pour cet agent'
                    : 'Monthly revenue target for this agent'}
                </p>
              </div>

              {/* Quick select buttons CA */}
              <div className="flex flex-wrap gap-2">
                {[100000, 250000, 500000, 750000, 1000000].map(val => (
                  <Button
                    key={val}
                    variant="outline"
                    size="sm"
                    onClick={() => setTargetValue(val.toString())}
                    className={targetValue === val.toString() ? 'border-[#2E7D32] bg-[#2E7D32]/10' : ''}
                  >
                    {val.toLocaleString('fr-FR')}
                  </Button>
                ))}
              </div>

              <div className="space-y-2 pt-3 border-t">
                <label className="text-sm font-medium text-[#0F172A]">
                  {language === 'fr' ? 'Objectif nombre de ventes / mois' : 'Sales count target / month'}
                </label>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={salesCountTargetValue}
                  onChange={(e) => setSalesCountTargetValue(e.target.value)}
                  placeholder={language === 'fr' ? 'Ex: 30' : 'Ex: 30'}
                  className="text-lg"
                  data-testid="sales-count-target-input"
                />
                <p className="text-xs text-[#78716C]">
                  {language === 'fr'
                    ? 'Score commercial = 80% atteinte CA + 20% atteinte nb ventes. Laissez 0 si non applicable.'
                    : 'Commercial score = 80% revenue + 20% sales count. Leave 0 if not applicable.'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {[10, 20, 30, 50, 80].map(val => (
                    <Button
                      key={val}
                      variant="outline"
                      size="sm"
                      onClick={() => setSalesCountTargetValue(val.toString())}
                      className={salesCountTargetValue === val.toString() ? 'border-[#2E7D32] bg-[#2E7D32]/10' : ''}
                    >
                      {val}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowTargetDialog(false)}
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              onClick={handleSaveTarget}
              disabled={savingTarget}
              className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white"
            >
              {savingTarget ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Enregistrer' : 'Save'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StatsPage;
